/**
 * AlertStatusBadge — color-coded status badge with hover popover explanation.
 *
 * When status is "Overdue", pass eventDateTime so the badge can distinguish
 * between yellow (>10 min) and red/critical (>20 min).
 */

import { useState, useRef } from 'react';

const CRITICAL_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes

const STATUS_CLASSES = {
    Waiting: 'na-status--waiting',
    Overdue: 'na-status--overdue',
    Accepted: 'na-status--accepted',
    Cleared: 'na-status--cleared',
    Arrived: 'na-status--arrived',
};

const STATUS_DESCRIPTIONS = {
    Waiting: 'Waiting for alert to be accepted or cleared if accept feature is N/A for device.',
    Overdue: 'Alert has been active over 5 minutes without being accepted or cleared.',
    OverdueCritical: 'Alert has been active over 10 minutes without being accepted or cleared.',
    Accepted: 'Alert has been accepted by a staff member but the device is still in alarm state.',
    Cleared: 'Device has been cleared. If this device is listed for AFR in system settings, it must be accepted and finished through the mobile app.',
    Arrived: 'Staff has accepted the alert and the device\'s alarm state has been cleared. Alert must be finished to close out.',
};

const AlertStatusBadge = ({ status, eventDateTime }) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const ref = useRef(null);

    // Determine if Overdue should be critical (>20 min)
    let isCritical = false;
    if (status === 'Overdue' && eventDateTime) {
        const eventTime = new Date(eventDateTime.endsWith?.('Z') ? eventDateTime : eventDateTime + 'Z').getTime();
        isCritical = Date.now() - eventTime > CRITICAL_THRESHOLD_MS;
    }

    const handleEnter = () => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const popoverWidth = 280;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceRight = window.innerWidth - rect.left;

            const horizontal = spaceRight < popoverWidth + 16
                ? { right: `${window.innerWidth - rect.right}px`, left: 'auto' }
                : { left: `${rect.left}px`, right: 'auto' };

            const vertical = spaceBelow < 100
                ? { bottom: `${window.innerHeight - rect.top + 6}px`, top: 'auto' }
                : { top: `${rect.bottom + 6}px`, bottom: 'auto' };

            setStyle({ position: 'fixed', ...horizontal, ...vertical });
        }
        setOpen(true);
    };

    const cls = (status === 'Overdue' && isCritical)
        ? 'na-status--overdue-critical'
        : (STATUS_CLASSES[status] || 'na-status--accepted');
    const desc = (status === 'Overdue' && isCritical)
        ? STATUS_DESCRIPTIONS.OverdueCritical
        : (STATUS_DESCRIPTIONS[status] || '');

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)}>
            <span className={`na-status ${cls}`}>{status || '—'}</span>
            {open && desc ? (
                <div className="na-status-popover" style={style}>{desc}</div>
            ) : null}
        </span>
    );
};

export default AlertStatusBadge;
